import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { AUTH_SESSION_CHANGING_EVENT, AUTH_SESSION_INVALID_EVENT, getAuthToken, isStoredSessionTokenCurrent } from '../utils/auth';

const API_URL = getApiUrl();

export const isCanonicalCustomerTrackingUrl = (value) => {
  try {
    const parsed = new URL(String(value || '').trim());
    return parsed.protocol === 'https:' &&
      parsed.hostname === 'entregas.brutosdelivery.cloud' &&
      parsed.port === '' &&
      parsed.username === '' &&
      parsed.password === '' &&
      parsed.pathname === '/acompanhar' &&
      parsed.search === '' &&
      /^#(?:m4_[A-Za-z0-9_-]{32}|(?!m4_)[A-Za-z0-9_-]{40,2048})$/.test(parsed.hash);
  } catch {
    return false;
  }
};

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return Boolean(document.execCommand?.('copy'));
  } finally {
    document.body.removeChild(textarea);
  }
};

const initialState = { status: 'idle', url: '', message: '', manualCopy: false };

const IfoodCustomerTrackingLink = ({ orderId, mode = 'ifood' }) => {
  const [state, setState] = useState(initialState);
  const requestRef = useRef(null);
  const expiresRef = useRef(0);
  const sessionRef = useRef(getAuthToken());
  const isCurrentSession = () => !sessionRef.current || (
    getAuthToken() === sessionRef.current && isStoredSessionTokenCurrent(sessionRef.current)
  );

  useEffect(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    expiresRef.current = 0;
    sessionRef.current = getAuthToken();
    setState(initialState);
    const clear = () => {
      requestRef.current?.abort(); requestRef.current = null; expiresRef.current = 0;
      sessionRef.current = getAuthToken(); setState(initialState);
    };
    window.addEventListener(AUTH_SESSION_CHANGING_EVENT, clear);
    window.addEventListener(AUTH_SESSION_INVALID_EVENT, clear);
    return () => {
      requestRef.current?.abort(); requestRef.current = null;
      window.removeEventListener(AUTH_SESSION_CHANGING_EVENT, clear);
      window.removeEventListener(AUTH_SESSION_INVALID_EVENT, clear);
    };
  }, [orderId, mode]);

  const copyLoadedLink = async (url, expectedRequest = null) => {
    if (!isCurrentSession() || (expectedRequest && requestRef.current !== expectedRequest)) return;
    try {
      const copied = await copyText(url);
      if (!isCurrentSession() || (expectedRequest && requestRef.current !== expectedRequest)) return;
      if (!copied) throw new Error('copy-unavailable');
      setState({
        status: 'success',
        url,
        message: mode === 'ifood' ? 'Link copiado. Cole no chat do iFood.' : 'Link copiado. Envie ao cliente.',
        manualCopy: false,
      });
    } catch {
      if (!isCurrentSession() || (expectedRequest && requestRef.current !== expectedRequest)) return;
      setState({
        status: 'success',
        url,
        message: mode === 'ifood'
          ? 'Não foi possível copiar automaticamente. Copie o link abaixo e cole no chat do iFood.'
          : 'Não foi possível copiar automaticamente. Copie o link abaixo e envie ao cliente.',
        manualCopy: true,
      });
    }
  };

  const generateAndCopy = async () => {
    if (state.status === 'loading') return;
    if (!isCurrentSession()) { setState(initialState); return; }
    if (state.url && (mode === 'ifood' || expiresRef.current > Date.now())) {
      await copyLoadedLink(state.url);
      return;
    }

    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    setState({ ...initialState, status: 'loading' });
    try {
      const response = await apiFetch(
        `${API_URL}/api/${mode === 'ifood' ? 'ifood/orders' : 'orders'}/${encodeURIComponent(orderId)}/customer-tracking-link`,
        { method: 'POST', cache: 'no-store', signal: controller.signal }
      );
      const body = await response.json().catch(() => ({}));
      if (requestRef.current !== controller || !isCurrentSession()) return;
      if (!response.ok) {
        const unavailable = [404, 409, 410].includes(response.status);
        setState({
          ...initialState,
          status: unavailable ? 'unavailable' : 'error',
          message: body?.error || (unavailable
            ? 'O rastreio não está disponível para este pedido.'
            : 'Não foi possível gerar o link de acompanhamento agora.'),
        });
        return;
      }
      if (!isCanonicalCustomerTrackingUrl(body?.trackingUrl)) {
        throw new Error('invalid-tracking-url');
      }
      expiresRef.current = Date.parse(body.expiresAt) || 0;
      if (mode !== 'ifood' && expiresRef.current <= Date.now()) throw new Error('expired-tracking-url');
      await copyLoadedLink(body.trackingUrl, controller);
    } catch (error) {
      if (error?.name === 'AbortError' || requestRef.current !== controller || !isCurrentSession()) return;
      setState({
        ...initialState,
        status: 'error',
        message: 'Não foi possível gerar o link de acompanhamento agora.',
      });
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  };

  const isLoading = state.status === 'loading';
  const isSuccess = state.status === 'success';
  const severity = state.status === 'unavailable' ? 'info' : state.status === 'error' ? 'error' : 'success';

  return (
    <Box mt={2} sx={{ display: 'grid', gap: 1 }} data-testid={mode === 'ifood' ? 'ifood-customer-tracking-link' : 'local-customer-tracking-link'}>
      <Typography variant="subtitle2">Acompanhamento Brutos Entregas</Typography>
      <Typography variant="body2" color="text.secondary">
        {mode === 'ifood'
          ? 'Gere o link e cole manualmente no chat do iFood para o cliente acompanhar a entrega.'
          : 'Copie o link de acompanhamento para enviar ao cliente. Este é o mesmo link usado no envio automático.'}
      </Typography>
      {state.message && <Alert severity={severity}>{state.message}</Alert>}
      {state.manualCopy && state.url && (
        <TextField
          label="Link de acompanhamento"
          value={state.url}
          fullWidth
          size="small"
          slotProps={{ htmlInput: { readOnly: true } }}
          onFocus={(event) => event.target.select()}
        />
      )}
      <Button
        type="button"
        variant="outlined"
        startIcon={isLoading ? <CircularProgress size={18} /> : <ContentCopyOutlinedIcon />}
        disabled={isLoading}
        onClick={generateAndCopy}
        sx={{ width: { xs: '100%', sm: 'fit-content' } }}
      >
        {isLoading ? 'Gerando link…' : isSuccess ? 'Copiar novamente' : state.status === 'error' ? 'Tentar novamente' : 'Gerar e copiar link'}
      </Button>
    </Box>
  );
};

export default IfoodCustomerTrackingLink;
